import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { AdFieldCurrencyEnum, CompletionStatusEnum, TransactionTypeEnum } from '../../types/enums';
import type { HandoverType } from '../../types/ads';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';

import { FormInputNumeric, LabelComponent } from '../uiComponents';
import { EditorSelectField } from './fields';
import { calculatePaymentAmounts, getCurrentHandover } from './propertyAdSale';
import styles from './PropertyAdEditor.module.scss';
import { usePropertyAdEditorNormalization } from './usePropertyAdEditorNormalization';

const currencyOptions = [
  { label: 'AED', value: AdFieldCurrencyEnum.AED },
  { label: 'USD', value: AdFieldCurrencyEnum.USD },
] as const;

const completionStatusOptions = [
  { label: 'Ready', value: CompletionStatusEnum.Ready },
  { label: 'Off-plan', value: CompletionStatusEnum.OffPlan },
] as const;

const quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

export const SalePropertyDetails = () => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<PropertyAdEditorFormValues>();
  const transactionType = useWatch({ control, name: 'transactionType' });
  const currency = useWatch({ control, name: 'currency' });
  const price = useWatch({ control, name: 'price' });
  const sellingPrice = useWatch({ control, name: 'sellingPrice' });
  const completionStatus = useWatch({ control, name: 'completionStatus' });
  const toSeller = useWatch({ control, name: 'toSeller' });
  const handover = useWatch({ control, name: 'handover' });
  const { changeCompletionStatus } = usePropertyAdEditorNormalization();

  const isReady = transactionType === TransactionTypeEnum.Ready;
  const isResale = transactionType === TransactionTypeEnum.Resale;
  const isDistress = transactionType === TransactionTypeEnum.Distress;
  const isDistressOffPlan = isDistress && completionStatus === CompletionStatusEnum.OffPlan;
  const showsPaymentFields = isResale || isDistressOffPlan;
  const relevantPrice = isDistress ? sellingPrice : price;

  if (!isReady && !isResale && !isDistress) {
    return null;
  }

  const updatePaymentAmounts = (nextPrice: number | null, nextToSeller: number | null) => {
    const paymentAmounts = calculatePaymentAmounts(nextPrice, nextToSeller);

    setValue('toSeller', paymentAmounts.toSeller, { shouldDirty: true });
    setValue('toDeveloper', paymentAmounts.toDeveloper, { shouldDirty: true });
  };

  const handlePriceChange = (value: number | null) => {
    setValue('price', value, { shouldDirty: true });

    if (isResale) {
      updatePaymentAmounts(value, toSeller);
    }
  };

  const handleSellingPriceChange = (value: number | null) => {
    setValue('sellingPrice', value, { shouldDirty: true });

    if (isDistress) {
      updatePaymentAmounts(value, toSeller);
    }
  };

  const handleToSellerChange = (value: number | null) => updatePaymentAmounts(relevantPrice, value);

  const setHandoverPart = (part: keyof HandoverType, value: string) => {
    const nextHandover = {
      quarter: handover?.quarter,
      year: handover?.year,
      [part]: value || undefined,
    };

    if (!nextHandover.quarter && !nextHandover.year) {
      setValue('handover', null, { shouldDirty: true });
      return;
    }

    setValue('handover', nextHandover, { shouldDirty: true });
  };

  const today = new Date();
  const currentHandover = getCurrentHandover(today);
  const currentYear = currentHandover.year;
  const currentQuarter = Number(currentHandover.quarter.slice(1));
  const defaultHandoverYears = Array.from({ length: 10 }, (_, index) =>
    (today.getFullYear() + index).toString()
  );
  const handoverYears = handover?.year
    ? Array.from(new Set([handover.year, ...defaultHandoverYears]))
    : defaultHandoverYears;

  const handleHandoverYearChange = (year: string) => {
    const quarterNumber = Number(handover?.quarter?.replace(/\D/g, ''));
    const safeQuarter =
      year === currentYear && quarterNumber > 0 && quarterNumber < currentQuarter
        ? `Q${currentQuarter}`
        : handover?.quarter;

    const nextHandover = { quarter: safeQuarter, year: year || undefined };
    setValue('handover', nextHandover.quarter || nextHandover.year ? nextHandover : null, {
      shouldDirty: true,
    });
  };

  return (
    <Box aria-label='Sale pricing' component='section' minWidth={0}>
      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          minWidth: 0,
          width: '100%',
        }}
      >
        {isDistress && (
          <EditorSelectField
            label='Completion status'
            name='completionStatus'
            options={completionStatusOptions}
            placeholder='Select completion status'
            onValueChange={changeCompletionStatus}
          />
        )}

        <EditorSelectField label='Currency' name='currency' options={currencyOptions} required />

        <Box className={styles.priceField} minWidth={0}>
          <FormInputNumeric
            label={isDistress ? 'Original price' : 'Price'}
            name='price'
            placeholder={isDistress ? 'Original price' : 'Price'}
            suffix={` ${currency}`}
            uiVariant='blank'
            valueType='number'
            required
            handleChange={handlePriceChange}
          />
        </Box>

        {isDistress && (
          <Box className={styles.priceField} minWidth={0}>
            <FormInputNumeric
              label='Selling price'
              name='sellingPrice'
              placeholder='Selling price'
              suffix={` ${currency}`}
              uiVariant='blank'
              valueType='number'
              required
              handleChange={handleSellingPriceChange}
            />
          </Box>
        )}

        {showsPaymentFields && (
          <>
            <Box className={styles.priceField} minWidth={0}>
              <FormInputNumeric
                label='To Seller'
                limiter={relevantPrice ?? 0}
                name='toSeller'
                placeholder='To Seller'
                readOnly={!relevantPrice || relevantPrice <= 0}
                suffix={` ${currency}`}
                uiVariant='blank'
                valueType='number'
                required
                handleChange={handleToSellerChange}
              />
            </Box>

            <Box className={styles.priceField} minWidth={0}>
              <FormInputNumeric
                label='To Developer'
                name='toDeveloper'
                placeholder='To Developer'
                readOnly
                suffix={` ${currency}`}
                uiVariant='blank'
                valueType='number'
              />
            </Box>

            <Box minWidth={0}>
              <LabelComponent label='Post handover' />
              <Controller
                control={control}
                name='postHandover'
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Select<string>
                      className={styles.selectControl}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Post handover' }}
                      value={field.value === null ? '' : String(field.value)}
                      renderValue={(value) => (
                        <Typography
                          className={value ? styles.selectValue : styles.selectPlaceholder}
                          component='span'
                          variant='inherit'
                        >
                          {value ? (value === 'true' ? 'Yes' : 'No') : 'Select value'}
                        </Typography>
                      )}
                      onChange={(event) => {
                        if (event.target.value !== '') {
                          field.onChange(event.target.value === 'true');
                        }
                      }}
                    >
                      {!isDistress && <MenuItem value=''>Select value</MenuItem>}
                      <MenuItem value='true'>Yes</MenuItem>
                      <MenuItem value='false'>No</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>

            <Box minWidth={0} sx={{ gridColumn: '1 / -1' }}>
              <LabelComponent label='Handover' />
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                  minWidth: 0,
                }}
              >
                <FormControl fullWidth>
                  <Select<string>
                    className={styles.selectControl}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Handover quarter' }}
                    value={handover?.quarter ?? ''}
                    renderValue={(value) => (
                      <Typography
                        className={value ? styles.selectValue : styles.selectPlaceholder}
                        component='span'
                        variant='inherit'
                      >
                        {value || 'Select quarter'}
                      </Typography>
                    )}
                    onChange={(event) => setHandoverPart('quarter', event.target.value)}
                  >
                    <MenuItem value=''>Select quarter</MenuItem>
                    {quarterOptions.map((quarter, index) => (
                      <MenuItem
                        disabled={handover?.year === currentYear && index + 1 < currentQuarter}
                        key={quarter}
                        value={quarter}
                      >
                        {quarter}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <Select<string>
                    className={styles.selectControl}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Handover year' }}
                    value={handover?.year ?? ''}
                    renderValue={(value) => (
                      <Typography
                        className={value ? styles.selectValue : styles.selectPlaceholder}
                        component='span'
                        variant='inherit'
                      >
                        {value || 'Select year'}
                      </Typography>
                    )}
                    onChange={(event) => handleHandoverYearChange(event.target.value)}
                  >
                    <MenuItem value=''>Select year</MenuItem>
                    {handoverYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {errors.sellingPrice?.message === '5%' && (
        <Typography
          color='#f00'
          component='p'
          fontSize={14}
          fontWeight={600}
          lineHeight='22px'
          margin='16px 0 0'
          role='alert'
        >
          Selling price must be at least 5% less than the Original price
        </Typography>
      )}
    </Box>
  );
};
